import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/services/api'
import { toast } from 'react-toastify'
import { parseVideoId, getEpornerVideoDetails, getLetsJerktvVideoDetails, getRedtubeVideoDetails } from '@/services/adultContent'
import './ContentPage.css'

interface LocalContent {
  id: number
  title: string
  description?: string
  status: string
  createdAt: string
  hlsPlaylistKey?: string
  duration?: number
  user: {
    id: number
    username?: string
    email: string
  }
}

interface ProviderVideoDetails {
  title?: string
  description?: string
  embed?: string
  embedUrl?: string
  iframeSrc?: string
  url?: string
  duration?: number | string
  views?: number
  rate?: string
  added?: string
  tags?: string[]
  categories?: string[]
  thumbnail?: string
}

const ContentPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [reportType, setReportType] = useState('')
  const [reportDescription, setReportDescription] = useState('')

  // Parser l'ID pour déterminer le provider
  const parsedId = id ? parseVideoId(id) : null

  // Cette page ne gère que les vidéos locales
  // Les autres providers (Eporner, LetsJerkTV, Redtube) ont leurs propres pages
  // Redirection de sécurité si jamais on arrive ici
  React.useEffect(() => {
    if (parsedId && parsedId.provider === 'eporner') {
      navigate(`/content/eporner/${id}`, { replace: true })
    } else if (parsedId && parsedId.provider === 'letsjerktv') {
      navigate(`/content/letsjerktv/${id}`, { replace: true })
    } else if (parsedId && parsedId.provider === 'redtube') {
      navigate(`/content/redtube/${id}`, { replace: true })
    }
  }, [parsedId, id, navigate])

  // Requête pour les vidéos locales
  const { data: localContent, isLoading: isLocalLoading, error: localError } = useQuery({
    queryKey: ['content', id],
    queryFn: async () => {
      const response = await api.get(`/contents/${id}`)
      return response.data as LocalContent
    },
    enabled: !!id && parsedId?.provider === 'local',
  })

  // Requête pour les vidéos Eporner
  const { data: epornerVideo, isLoading: isEpornerLoading, error: epornerError } = useQuery({
    queryKey: ['eporner-video', parsedId?.videoId],
    queryFn: async () => {
      if (!parsedId?.videoId) throw new Error('ID de vidéo manquant')
      return await getEpornerVideoDetails(parsedId.videoId) as ProviderVideoDetails
    },
    enabled: !!parsedId && parsedId.provider === 'eporner',
  })

  // Requête pour les vidéos LetsJerktv - nécessite l'URL complète
  // On devra récupérer l'URL depuis le contexte ou la passer en paramètre
  const { data: letsjerktvVideo, isLoading: isLetsJerktvLoading, error: letsjerktvError } = useQuery({
    queryKey: ['letsjerktv-video', parsedId?.videoId],
    queryFn: async () => {
      if (!parsedId?.videoId) throw new Error('ID de vidéo manquant')
      // Pour LetsJerktv, on a besoin de l'URL complète, pas juste l'ID
      // On va tenter de construire l'URL ou récupérer depuis les paramètres
      const videoUrl = `https://letsjerk.tv/${parsedId.videoId}`
      return await getLetsJerktvVideoDetails(videoUrl) as ProviderVideoDetails
    },
    enabled: !!parsedId && parsedId.provider === 'letsjerktv',
  })

  // Requête pour les vidéos Redtube
  const { data: redtubeVideo, isLoading: isRedtubeLoading, error: redtubeError } = useQuery({
    queryKey: ['redtube-video', parsedId?.videoId],
    queryFn: async () => {
      if (!parsedId?.videoId) throw new Error('ID de vidéo manquant')
      return await getRedtubeVideoDetails(parsedId.videoId) as ProviderVideoDetails
    },
    enabled: !!parsedId && parsedId.provider === 'redtube',
  })

  // URL HLS pour les vidéos locales
  const { data: hlsUrl } = useQuery({
    queryKey: ['content', id, 'hls'],
    queryFn: async () => {
      const response = await api.get(`/contents/${id}/hls-url`)
      return response.data.url as string
    },
    enabled: !!id && parsedId?.provider === 'local' && localContent?.status === 'APPROVED',
  })

  // Déterminer quel contenu utiliser
  const isLoading = parsedId?.provider === 'local' ? isLocalLoading : 
                    parsedId?.provider === 'eporner' ? isEpornerLoading :
                    parsedId?.provider === 'letsjerktv' ? isLetsJerktvLoading :
                    parsedId?.provider === 'redtube' ? isRedtubeLoading : false

  const error = parsedId?.provider === 'local' ? localError :
                parsedId?.provider === 'eporner' ? epornerError :
                parsedId?.provider === 'letsjerktv' ? letsjerktvError :
                parsedId?.provider === 'redtube' ? redtubeError : null

  const content = parsedId?.provider === 'local' ? localContent :
                  parsedId?.provider === 'eporner' ? epornerVideo :
                  parsedId?.provider === 'letsjerktv' ? letsjerktvVideo :
                  parsedId?.provider === 'redtube' ? redtubeVideo : null

  const handleReport = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Le signalement n'est disponible que pour les vidéos locales
    if (parsedId?.provider !== 'local') {
      toast.error('Le signalement n\'est disponible que pour les vidéos locales')
      return
    }
    
    if (!reportType) {
      toast.error('Veuillez sélectionner un type de signalement')
      return
    }

    try {
      await api.post(`/contents/${id}/report`, {
        type: reportType,
        description: reportDescription,
      })
      
      toast.success('Signalement envoyé')
      setReportType('')
      setReportDescription('')
    } catch (error) {
      toast.error('Erreur lors du signalement')
    }
  }

  // Fonction pour formater la durée
  const formatDuration = (duration?: number | string): string => {
    if (!duration) return ''
    if (typeof duration === 'string') {
      // Format "mm:ss" ou secondes
      if (duration.includes(':')) return duration
      const seconds = parseInt(duration, 10)
      if (!isNaN(seconds)) {
        return `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`
      }
    }
    return `${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')}`
  }

  // Fonction pour extraire l'URL src d'un code iframe embed
  const extractIframeSrc = (embedCode: string): string | null => {
    // Rechercher l'attribut src dans le code embed
    const srcMatch = embedCode.match(/src=["']([^"']+)["']/i)
    if (srcMatch && srcMatch[1]) {
      return srcMatch[1]
    }
    // Si c'est directement une URL
    if (embedCode.startsWith('http://') || embedCode.startsWith('https://')) {
      return embedCode
    }
    return null
  }

  // Fonction pour obtenir la source de la vidéo selon le provider
  const getVideoSource = () => {
    if (parsedId?.provider === 'local' && hlsUrl) {
      return { type: 'hls', url: hlsUrl }
    }
    if (parsedId?.provider === 'eporner') {
      // Essayer d'extraire l'URL depuis le code embed
      if (epornerVideo?.embed) {
        const iframeSrc = extractIframeSrc(epornerVideo.embed)
        if (iframeSrc) {
          return { type: 'iframe', src: iframeSrc }
        }
        // Sinon utiliser le code embed directement
        return { type: 'embed', code: epornerVideo.embed }
      }
      // Fallback sur l'URL si disponible
      if (epornerVideo?.url) {
        return { type: 'iframe', src: epornerVideo.url }
      }
    }
    if (parsedId?.provider === 'letsjerktv' && letsjerktvVideo?.iframeSrc) {
      return { type: 'iframe', src: letsjerktvVideo.iframeSrc }
    }
    if (parsedId?.provider === 'redtube') {
      if (redtubeVideo?.embedUrl) {
        const iframeSrc = extractIframeSrc(redtubeVideo.embedUrl)
        if (iframeSrc) {
          return { type: 'iframe', src: iframeSrc }
        }
        return { type: 'embed', code: redtubeVideo.embedUrl }
      }
      // Fallback sur l'URL si disponible
      if (redtubeVideo?.url) {
        return { type: 'iframe', src: redtubeVideo.url }
      }
    }
    return null
  }

  if (isLoading) {
    return <div className="content-page"><div className="content-page-loading">Chargement...</div></div>
  }

  if (error || !content) {
    return <div className="content-page"><div className="content-page-error">Contenu non trouvé</div></div>
  }

  // Vérifier le statut uniquement pour les vidéos locales
  if (parsedId?.provider === 'local' && (content as LocalContent).status !== 'APPROVED') {
    return <div className="content-page"><div className="content-page-error">Ce contenu n'est pas disponible</div></div>
  }

  const videoSource = getVideoSource()
  const title = parsedId?.provider === 'local' 
    ? (content as LocalContent).title 
    : (content as ProviderVideoDetails)?.title || 'Sans titre'
  const description = parsedId?.provider === 'local'
    ? (content as LocalContent).description
    : (content as ProviderVideoDetails)?.description
  const duration = parsedId?.provider === 'local'
    ? (content as LocalContent).duration
    : (content as ProviderVideoDetails)?.duration

  const location = useLocation()
  const pageTitle = `${title} - EdenPlaisir.com`
  const pageDescription = description || `Regardez ${title} sur EdenPlaisir.com`
  const canonicalUrl = `https://www.edenplaisir.com${location.pathname}`.replace(/\/$/, '').split('?')[0] || `https://www.edenplaisir.com/content/${id}`
  
  return (
    <div className="content-page">
      <Helmet>
        <title>{pageTitle}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="noindex, follow" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="video.movie" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="player" />
      </Helmet>
      
      <div className="content-page-header">
        <h1 className="content-page-title">{title}</h1>
        {description && (
          <p className="content-page-description">{description}</p>
        )}
      </div>

      {/* Métadonnées */}
      <div className="content-page-meta">
        {parsedId?.provider === 'local' && (content as LocalContent).user && (
          <div className="content-page-meta-item">
            <strong>Créateur:</strong>
            <span>{(content as LocalContent).user.username || (content as LocalContent).user.email}</span>
          </div>
        )}
        {parsedId?.provider === 'local' && (content as LocalContent).createdAt && (
          <div className="content-page-meta-item">
            <strong>Date:</strong>
            <span>{new Date((content as LocalContent).createdAt).toLocaleDateString('fr-FR')}</span>
          </div>
        )}
        {(content as ProviderVideoDetails)?.added && (
          <div className="content-page-meta-item">
            <strong>Ajouté:</strong>
            <span>{(content as ProviderVideoDetails).added}</span>
          </div>
        )}
        {(content as ProviderVideoDetails)?.views !== undefined && (
          <div className="content-page-meta-item">
            <strong>Vues:</strong>
            <span>{(content as ProviderVideoDetails).views?.toLocaleString()}</span>
          </div>
        )}
        {(content as ProviderVideoDetails)?.rate && (
          <div className="content-page-meta-item">
            <strong>Note:</strong>
            <span>{(content as ProviderVideoDetails).rate}</span>
          </div>
        )}
        {duration && (
          <div className="content-page-meta-item">
            <strong>Durée:</strong>
            <span>{formatDuration(duration)}</span>
          </div>
        )}
      </div>

      {/* Layout principal avec vidéo et sidebar */}
      <div className="content-page-main">
        <div className="content-page-main-content">
          {/* Affichage de la vidéo selon le type */}
          {videoSource && (
            <div className="content-page-video-container">
              {videoSource.type === 'hls' && (
                <video
                  controls
                  width="100%"
                  height="100%"
                >
                  <source src={videoSource.url} type="application/x-mpegURL" />
                  Votre navigateur ne supporte pas la lecture HLS.
                </video>
              )}
              {videoSource.type === 'embed' && (
                <div dangerouslySetInnerHTML={{ __html: videoSource.code }} />
              )}
              {videoSource.type === 'iframe' && (
                <iframe
                  src={videoSource.src}
                  width="100%"
                  height="100%"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  title={title}
                />
              )}
            </div>
          )}
          
          {!videoSource && parsedId?.provider === 'eporner' && (
            <div className="content-page-debug">
              <p style={{ color: '#ccc' }}>Chargement de la vidéo...</p>
              {epornerVideo && (
                <pre>{JSON.stringify(epornerVideo, null, 2)}</pre>
              )}
            </div>
          )}
        </div>

        <div className="content-page-sidebar">
          {/* Tags et catégories pour les vidéos de providers */}
          {(content as ProviderVideoDetails)?.tags && (content as ProviderVideoDetails).tags!.length > 0 && (
            <div className="content-page-tags">
              <h3>Tags</h3>
              <div className="content-page-tag-list">
                {(content as ProviderVideoDetails).tags!.map((tag, index) => (
                  <span 
                    key={index} 
                    className="content-page-tag clickable"
                    onClick={() => navigate('/categories', { state: { filterTag: tag } })}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(content as ProviderVideoDetails)?.categories && (content as ProviderVideoDetails).categories!.length > 0 && (
            <div className="content-page-categories">
              <h3>Catégories</h3>
              <div className="content-page-category-list">
                {(content as ProviderVideoDetails).categories!.map((category, index) => (
                  <span key={index} className="content-page-category">{category}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Formulaire de signalement uniquement pour les vidéos locales */}
      {parsedId?.provider === 'local' && (
        <div className="content-page-report">
          <h3>Signaler ce contenu</h3>
          <form onSubmit={handleReport}>
            <div className="form-group">
              <label htmlFor="reportType">Type de signalement :</label>
              <select
                id="reportType"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                required
              >
                <option value="">Sélectionner un type</option>
                <option value="INAPPROPRIATE">Contenu inapproprié</option>
                <option value="COPYRIGHT">Violation de droits d'auteur</option>
                <option value="SPAM">Spam</option>
                <option value="VIOLENCE">Violence</option>
                <option value="OTHER">Autre</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="reportDescription">Description (optionnel) :</label>
              <textarea
                id="reportDescription"
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                rows={4}
              />
            </div>

            <button type="submit" className="btn-secondary">
              Envoyer le signalement
            </button>
          </form>
        </div>
      )}
    </div>
  )
}

export default ContentPage
